import { Preferences } from '@capacitor/preferences';

/**
 * Local persistence.
 *
 * Capacitor's Preferences plugin, which is one of the three native capabilities
 * the store listing rests on (alongside haptics and push): on Android it writes
 * to SharedPreferences and on iOS to UserDefaults, so the device identity and
 * settings survive an app update rather than living in a web view's storage
 * that the OS is free to clear.
 *
 * On the web the plugin falls back to localStorage on its own, so callers never
 * branch on platform. The try/catch is for the case localStorage itself throws:
 * private mode, or a browser configured to block site data.
 */
export const storage = {
  async get(key: string): Promise<string | null> {
    try {
      const { value } = await Preferences.get({ key });
      return value;
    } catch {
      return null;
    }
  },

  async set(key: string, value: string): Promise<void> {
    try {
      await Preferences.set({ key, value });
    } catch {
      // Losing a preference is survivable; failing to start is not.
    }
  },

  async remove(key: string): Promise<void> {
    try {
      await Preferences.remove({ key });
    } catch {
      /* ignore */
    }
  },

  async getJson<T>(key: string, fallback: T): Promise<T> {
    const raw = await this.get(key);
    if (raw === null) return fallback;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  },

  async setJson(key: string, value: unknown): Promise<void> {
    await this.set(key, JSON.stringify(value));
  },
};
